import { inject, Injectable } from '@angular/core';
import { BannerService } from '@terminus-ui/banner';
import { applicationManagerMessages } from 'grpc-gateway-generated';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ApplicationManagerApiService } from './application-manager-api.service';
@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  private applicationManagerApi = inject(ApplicationManagerApiService);
  private bannerService = inject(BannerService);

  private fetchedAllCustomers = false;
  private customerCollection: Map<string, applicationManagerMessages.Customer> =
    new Map();
  private customerUserCollection: Map<
    string,
    applicationManagerMessages.User[]
  > = new Map();
  private customerAppInstanceCollection: Map<
    string,
    applicationManagerMessages.Instance[]
  > = new Map();

  /** if the collection of customers has been retrieved just return it,
   * otherwise fetch it and store a collection of customers
   */
  getAllCustomers(
    status = 'ANY',
    refresh = false,
  ): Observable<applicationManagerMessages.Customer[]> {
    if (this.fetchedAllCustomers && !refresh)
      return of([...this.customerCollection.values()]);

    return this.applicationManagerApi.listAllCustomers(status).pipe(
      /** on retrieval save the customers in a map */
      tap((customers) => {
        this.fetchedAllCustomers = true;
        this.customerCollection = new Map(
          customers.map((customer) => [
            customer.customerId || '',
            customer as applicationManagerMessages.Customer,
          ]),
        );
      }),
      catchError((err) => {
        console.error('Unable to load customers.', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to load customers.`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        return of(<applicationManagerMessages.Customer[]>[]);
      }),
    ) as Observable<applicationManagerMessages.Customer[]>;
  }

  /** if a customer is stored, return it otherwise fetch */
  getCustomerById(
    customerId: string,
    refresh = false,
  ): Observable<applicationManagerMessages.Customer | undefined> {
    if (!customerId) {
      console.error('No customerId provided to getCustomerById');
      return of(undefined);
    }
    if (!refresh && this.customerCollection.has(customerId))
      return of(
        this.customerCollection.get(
          customerId,
        ) as applicationManagerMessages.Customer,
      );

    return this.applicationManagerApi.getCustomerById(customerId).pipe(
      catchError((err) => {
        console.error('Unable to load customers.', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to load customer.`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        return of({} as applicationManagerMessages.CustomerResponse);
      }),
      tap((customerResponse) => {
        if (customerResponse.customer)
          this.customerCollection.set(customerId, customerResponse.customer);
        if (customerResponse.instances)
          this.customerAppInstanceCollection.set(
            customerId,
            customerResponse.instances,
          );
      }),
      map(
        (customerResponse) =>
          customerResponse.customer as applicationManagerMessages.Customer,
      ),
    );
  }

  getCustomerUsers(customerId: string, refresh = false) {
    if (this.customerUserCollection.has(customerId) && !refresh)
      return of(
        this.customerUserCollection.get(
          customerId,
        ) as applicationManagerMessages.User[],
      );

    return this.applicationManagerApi.listAllCustomerUsers(customerId).pipe(
      catchError((err) => {
        console.error('Unable to load customer users.', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to load customers.`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        return of(<applicationManagerMessages.User[]>[]);
      }),
      /** on retrieval save the users in a map */
      tap((users) => this.customerUserCollection.set(customerId, users)),
    ) as Observable<applicationManagerMessages.User[]>;
  }

  /** customer app instances are returned with the response to getCustomer. */
  getCustomerAppInstances(customerId: string, refresh = false) {
    if (this.customerAppInstanceCollection.has(customerId) && !refresh)
      return of(this.customerAppInstanceCollection.get(customerId));

    return this.getCustomerById(customerId, true).pipe(
      switchMap(() => of(this.customerAppInstanceCollection.get(customerId))),
    );
  }

  toggleUserAccess(userId: number, instanceId: string, hasAccess: boolean) {
    return this.applicationManagerApi
      .toggleUserAccess(userId, instanceId, hasAccess)
      .pipe(
        catchError((err) => {
          console.error('Unable to toggle user access.', err);
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Unable to toggle user access.`,
            type: 'warning',
            enableAutoDismissWithDuration: 10000,
          });
          return of({});
        }),
      );
  }

  toggleUserAdmin(customerId: string, userId: number, isAdmin: boolean) {
    return this.applicationManagerApi
      .toggleUserAdmin(customerId, userId, isAdmin)
      .pipe(
        catchError((err) => {
          console.error('Unable to toggle user admin.', err);
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Unable to toggle user admin.`,
            type: 'warning',
            enableAutoDismissWithDuration: 10000,
          });
          return of({});
        }),
      );
  }

  updateUser(userId: number, email: string, name: string) {
    return this.applicationManagerApi.updateUser(userId, email, name).pipe(
      catchError((err) => {
        console.error('Unable to update user.', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to update user.`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        return of({});
      }),
    );
  }

  deleteUser(customerId: string, userId: number) {
    return this.applicationManagerApi
      .toggleDisabledUser(customerId, userId, true)
      .pipe(
        catchError((err) => {
          console.error('Unable to delete user.', err);
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Unable to delete user.`,
            type: 'warning',
            enableAutoDismissWithDuration: 10000,
          });
          return of({});
        }),
      );
  }

  addUsers(customerId: string, users: applicationManagerMessages.User[]) {
    return this.applicationManagerApi.addUsers(customerId, users).pipe(
      catchError((err) => {
        console.error('Unable to add users.', err);
        this.bannerService.pushBanner({
          isClosable: true,
          isFloating: false,
          title: `Unable to add users.`,
          type: 'warning',
          enableAutoDismissWithDuration: 10000,
        });
        return of({});
      }),
    );
  }
}
